import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { useIdleLogout } from "./useIdleLogout";
import { initPostHog, identifyPostHog, resetPostHog, bindPostHogConsent } from "./posthog";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      identifyPostHog(data);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    // skip /me check if returning from Google OAuth — AuthCallback handles it
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Boot analytics (respects consent) + wire consent listener once at app start.
    initPostHog();
    bindPostHogConsent();
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  // ── Session-expired listener ─────────────────────────────────────────────
  // api.js dispatches "aif-session-expired" when the refresh token is dead.
  // Clear user context; ProtectedRoute will redirect to /login.
  useEffect(() => {
    const onExpired = () => {
      resetPostHog();
      setUser(null);
    };
    window.addEventListener("aif-session-expired", onExpired);
    return () => window.removeEventListener("aif-session-expired", onExpired);
  }, []);

  const login = async (email, password, remember_me = false) => {
    // If the login page is on a `{slug}.aifledger.com` host (or ?practice=<slug>
    // for previews), pass the slug so the backend rejects a cross-practice
    // login instead of silently logging into a mismatched tenant.
    let expected_practice_slug = null;
    try {
      const host = window.location.hostname || "";
      const q = new URLSearchParams(window.location.search).get("practice");
      const parts = host.split(".");
      if (parts.length >= 3 && ["aifledger", "corpzo"].some((k) => host.includes(k))) {
        const first = parts[0].toLowerCase();
        const reserved = ["www", "app", "auth", "api", "cdn", "admin",
                          "preview", "fundops-india"];
        if (first && !reserved.includes(first)) {
          expected_practice_slug = first;
        }
      }
      if (q) expected_practice_slug = q.trim().toLowerCase();
    } catch { /* ignore */ }
    const body = { email, password, remember_me };
    if (expected_practice_slug) body.expected_practice_slug = expected_practice_slug;
    const { data } = await api.post("/auth/login", body);
    // If MFA is required the backend returns { mfa_required: true, email, name }
    // WITHOUT issuing auth cookies. Do NOT populate the user context yet —
    // Login.jsx's redirect effect would otherwise navigate to /dashboard
    // unauthenticated and bounce back to /login, losing the MFA prompt.
    if (data.mfa_required) return data;
    setUser(data);
    identifyPostHog(data);
    try {
      const { trackEvent } = await import("./posthog");
      trackEvent("login_succeeded", {
        active_role: data.active_role,
        roles_count: (data.roles || []).length,
        must_change_password: !!data.must_change_password,
        must_enroll_mfa: !!data.must_enroll_mfa,
      });
    } catch { /* ignore */ }
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore network errors */ }
    resetPostHog();
    setUser(null);
  };

  const switchRole = async (role) => {
    const { data } = await api.post("/auth/switch-role", { role });
    setUser(data);
    return data;
  };

  const finishGoogle = async (sessionId) => {
    const { data } = await api.post("/auth/google/session", { session_id: sessionId });
    setUser(data);
    return data;
  };

  // 30-minute idle → auto-logout → redirect to home ("/"). Only active when signed in.
  useIdleLogout(user, logout);

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, login, logout, switchRole, refresh, finishGoogle }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}

export const ROLE_LABELS = {
  platform_admin: "Owner",
  super_admin: "Admin",
  fund_admin: "Sub-Admin",
  accountant: "Accountant",
  compliance_officer: "Compliance Officer",
  fund_manager: "Fund Manager",
  trustee_viewer: "Trustee Viewer",
  auditor_viewer: "Auditor Viewer",
  rta_viewer: "RTA Viewer",
  investor: "Investor / LP",
  relationship_manager: "Relationship Manager",
  distributor: "Distributor",
};

// Authority hierarchy — used to sort the Role Switcher dropdown from
// most-powerful → least. Any role not listed here sinks to the bottom.
export const ROLE_ORDER = [
  "platform_admin",
  "super_admin",
  "fund_admin",
  "accountant",
  "compliance_officer",
  "fund_manager",
  "relationship_manager",
  "trustee_viewer",
  "auditor_viewer",
  "rta_viewer",
  "investor",
  "distributor",
];
